import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "../lib/supabase";

/** Affiché lorsque la session existe mais l’email n’est pas encore confirmé (Auth par email). */
export default function AuthVerificationEmail() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const u = data.session?.user;
      if (!u?.email) {
        navigate("/auth", { replace: true });
        return;
      }
      if (u.email_confirmed_at) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setEmail(u.email);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const resend = async () => {
    if (!email) return;
    setBusy(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth` },
      });
      if (error) throw error;
      setMsg({ type: "ok", text: "Email de confirmation renvoyé." });
    } catch (e) {
      setMsg({ type: "err", text: e instanceof Error ? e.message : "Échec de l'envoi" });
    } finally {
      setBusy(false);
    }
  };

  const checkAgain = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await supabase.auth.refreshSession();
      const { data } = await supabase.auth.getSession();
      if (data.session?.user?.email_confirmed_at) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setMsg({ type: "err", text: "L'email n'est pas encore confirmé. Vérifiez votre boîte de réception." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-xl font-bold text-slate-900">Confirmez votre email</h1>
        <p className="mt-3 text-sm text-slate-600">
          Un lien de confirmation a été envoyé à <strong className="text-slate-800">{email ?? "…"}</strong>. Cliquez sur ce lien pour activer votre compte, puis revenez sur cette page.
        </p>
        {msg && (
          <p className={`mt-4 text-sm ${msg.type === "ok" ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>
        )}
        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            disabled={busy || !email}
            onClick={resend}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Renvoyer l&apos;email
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={checkAgain}
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            J&apos;ai confirmé — actualiser la session
          </button>
          <Link to="/auth" className="text-center text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline">
            Retour connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
